import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SurveyDialog } from '.';
import { surveyAnswerRequestAction, surveyDeclineRequestAction } from '../../../data/actions/foundation';
import { rememberSurveyDismissed, wasSurveyDismissed } from '../../../helper/survey';

/**
 * Decides whether the mandatory survey pops up after login and wires the actions.
 * The survey itself comes from the startup response; it is only delivered while the
 * user has neither answered it nor opted out.
 */
export const ConnectedSurveyDialog: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector(state => state.foundation?.auth);
  const anon = useSelector(state => state.foundation?.anon);
  const updateTermsRules = useSelector(state => state.foundation?.updatetermsrules);
  const setTermsRules = useSelector(state => state.foundation?.settermsrules);
  const conferencingVisible = useSelector(state => state.conferencing?.visible);
  const profile = useSelector(state => state.foundation?.profile);
  const survey = useSelector(state => state.foundation?.survey);
  const [open, setOpen] = useState(false);
  // Which survey was already offered in this page load, so a dismiss is not undone by
  // the next re-render or startup refetch. This component is mounted permanently in
  // AppFrame and never unmounts on logout, so it has to be reset when the session ends
  // — otherwise a logout and login in the same tab would not offer the survey again.
  const shownForSurveyId = useRef<string | null>(null);

  const eligible =
    !!survey?.id &&
    !!survey?.answers?.length &&
    !!auth &&
    !anon &&
    !updateTermsRules &&
    !setTermsRules &&
    !conferencingVisible &&
    !!profile?.userId &&
    !wasSurveyDismissed(survey.id);

  useEffect(() => {
    // Session ended (logout, deauth, expiry): forget what was offered, so the next
    // login starts over.
    if (!auth || anon) {
      shownForSurveyId.current = null;
    }
  }, [auth, anon]);

  useEffect(() => {
    if (eligible && survey?.id && shownForSurveyId.current !== survey.id) {
      shownForSurveyId.current = survey.id;
      setOpen(true);
    }
  }, [eligible, survey]);

  const handleSubmit = (answerIds: Array<string>) => {
    if (!survey?.id || !answerIds.length) return;
    // answerIds are in the order the user ticked them: the last ticked answer with a link wins.
    const redirect = [...answerIds]
      .reverse()
      .map(id => survey.answers.find(a => a.answerId === id)?.link)
      .find(link => !!link?.id) || undefined;
    dispatch(surveyAnswerRequestAction({ surveyId: survey.id, answerIds, redirect, history }));
    setOpen(false);
  };

  const handleDecline = () => {
    if (!survey?.id) return;
    // Explicit opt-out — stored as a SurveyResult, so it never comes back.
    dispatch(surveyDeclineRequestAction({ surveyId: survey.id }));
    setOpen(false);
  };

  const handleDismiss = () => {
    // Close icon / outside click: nothing is stored server-side, so the survey is
    // presented again at the next login. Remembering it for this browser tab keeps a
    // page reload from re-opening it straight away; logging in or out clears it.
    if (survey?.id) rememberSurveyDismissed(survey.id);
    setOpen(false);
  };

  return <SurveyDialog open={open} survey={survey} onSubmit={handleSubmit} onDecline={handleDecline} onDismiss={handleDismiss} />;
};
